import React from "react";
import { Link } from "react-router-dom";

const S = ({ title, children }) => (
  <section className="mb-6">
    <h2 className="font-heading text-xl font-semibold text-forest mb-2">{title}</h2>
    <div className="text-sm text-ink/85 leading-relaxed space-y-2">{children}</div>
  </section>
);

export function Terms() {
  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      <h1 className="font-heading text-3xl font-semibold text-forest mb-2">Terms of Service</h1>
      <p className="text-xs text-muted-foreground mb-8">Last updated: Feb 2026</p>

      <S title="1. Acceptance">
        <p>By using Apexture ("the platform"), you agree to these Terms. If you do not agree, please do not use the platform.</p>
      </S>
      <S title="2. What Apexture is">
        <p>Apexture is an <strong>educational fitness and posture-awareness platform</strong>. We provide screening tools, exercise information, and awareness content. We do not diagnose, treat, cure, or prescribe medical care.</p>
      </S>
      <S title="3. Not medical advice">
        <p>All content is for general educational purposes. Always consult a licensed physiotherapist or physician for medical concerns. See our <Link to="/disclaimer" className="underline">Medical Disclaimer</Link>.</p>
      </S>
      <S title="4. Accounts">
        <p>You must provide accurate information when registering. You are responsible for keeping your password safe. Notify us immediately of any unauthorised access.</p>
      </S>
      <S title="5. Payments & subscription">
        <p>The launch price is ₹199 for 90 days of full access. Prices may change with notice. Payments are processed by Cashfree; we do not store card details. See our <Link to="/refund" className="underline">Refund Policy</Link>.</p>
      </S>
      <S title="6. Acceptable use">
        <p>Do not attempt to reverse-engineer, scrape, redistribute, or misuse the platform. Do not share account access with others.</p>
      </S>
      <S title="7. Limitation of liability">
        <p>To the maximum extent permitted by law, Apexture and its team are not liable for injuries, losses, or damages resulting from use of the platform. You use the exercises and content at your own risk.</p>
      </S>
      <S title="8. Governing law">
        <p>These Terms are governed by the laws of India. Any disputes shall be subject to the jurisdiction of the courts of Uttarakhand, India.</p>
      </S>
      <S title="9. Contact">
        <p>Questions? Email <a className="underline" href="mailto:path@truepathcareer.com">path@truepathcareer.com</a>.</p>
      </S>
    </div>
  );
}

export function Privacy() {
  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      <h1 className="font-heading text-3xl font-semibold text-forest mb-2">Privacy Policy</h1>
      <p className="text-xs text-muted-foreground mb-8">Last updated: Feb 2026</p>

      <S title="1. What we collect">
        <p>When you sign up, we collect your name, email, and a hashed password. When you complete an assessment, we store your posture pattern, muscle test results, and generated plan (for signed-in paying users). When you pay, Cashfree collects payment info directly — we only store the order ID and status.</p>
      </S>
      <S title="2. Photos">
        <p>Photos you upload for AI analysis are processed once and <strong>not stored</strong> on our servers.</p>
      </S>
      <S title="3. How we use it">
        <p>To provide the service, generate your report, save your progress, respond to feedback, and improve the platform. We never sell your data.</p>
      </S>
      <S title="4. Third parties">
        <p>Cashfree (payments), OpenAI (AI analysis via a secure server-side call), and hosting infrastructure. All are contract-bound and provide equivalent privacy protections.</p>
      </S>
      <S title="5. Your rights">
        <p>You can request data export or deletion anytime by emailing our Grievance Officer.</p>
      </S>
      <S title="6. Grievance Officer">
        <p>Email: <a className="underline" href="mailto:path@truepathcareer.com">path@truepathcareer.com</a>. Responds within 15 business days as required by IT Rules 2021.</p>
      </S>
      <S title="7. Cookies">
        <p>We use a single httpOnly session cookie for login. No tracking or advertising cookies.</p>
      </S>
    </div>
  );
}

export function Refund() {
  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      <h1 className="font-heading text-3xl font-semibold text-forest mb-2">Refund Policy</h1>
      <p className="text-xs text-muted-foreground mb-8">Last updated: Feb 2026</p>
      <S title="1. 7-day refund window">
        <p>If you are not satisfied within 7 days of payment, contact us for a full refund. No questions asked.</p>
      </S>
      <S title="2. How to request">
        <p>Email <a className="underline" href="mailto:path@truepathcareer.com">path@truepathcareer.com</a> with your registered email and order ID. We process refunds within 5-7 business days back to the original payment method.</p>
      </S>
      <S title="3. After 7 days">
        <p>Refunds after 7 days are considered case-by-case at our discretion.</p>
      </S>
      <S title="4. Access on refund">
        <p>Once a refund is processed, your access will be revoked immediately.</p>
      </S>
    </div>
  );
}

export function Disclaimer() {
  return (
    <div className="max-w-3xl mx-auto animate-fade-in">
      <h1 className="font-heading text-3xl font-semibold text-forest mb-2">Medical Disclaimer</h1>
      <p className="text-xs text-muted-foreground mb-8">Please read carefully</p>
      <div className="bg-terracotta/10 border border-terracotta/30 rounded-lg p-5 mb-6 text-sm">
        <p className="font-medium text-terracotta mb-2">Apexture is not a medical service.</p>
        <p>Apexture is an educational fitness and posture-awareness platform. It is <strong>not a substitute for professional medical diagnosis, examination, or treatment</strong>. Do not use it to diagnose or treat any health condition.</p>
      </div>
      <S title="When to see a doctor immediately">
        <p>Stop using Apexture and seek in-person medical care immediately if you experience: numbness or weakness spreading down your arms or legs; loss of bladder/bowel control; groin numbness; sudden severe (worst-ever) headache; recent significant trauma to the spine; unexplained weight loss with night pain.</p>
      </S>
      <S title="Exercise safety">
        <p>Exercise videos and instructions are third-party or general content. Stop immediately if you feel sharp pain, dizziness, or unusual symptoms. Consult a licensed physiotherapist if you are unsure whether an exercise is appropriate for you.</p>
      </S>
      <S title="No doctor-patient relationship">
        <p>Using Apexture does not create a doctor-patient or physiotherapist-patient relationship. For personalised medical advice, consult a licensed clinician in person.</p>
      </S>
      <S title="Pregnancy, existing conditions, minors">
        <p>If you are pregnant, have a serious medical condition, are recovering from surgery, or are under 18, consult a doctor before doing any exercises on this platform.</p>
      </S>
    </div>
  );
}
